import { isPlatformBrowser } from '@angular/common';
import type {
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  inject,
  Input,
  PLATFORM_ID,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { shakeAnimation } from '@freelancer/animations';
import type { Currency } from '@freelancer/datastore/collections';
import {
  FreelancerCurrencyPipe,
  type AmountRange,
  type CurrencyFormatOptions,
} from '@freelancer/pipes';
import { ScreenSizeService } from '@freelancer/screen-size';
import { IconModule } from '@freelancer/ui/icon';
import { InputComponent } from '@freelancer/ui/input';
import { ValidationErrorComponent } from '@freelancer/ui/validation-error';
import { isDefined } from '@freelancer/utils';
import { ProjectTypeApi } from 'api-typings/projects/projects';
import {
  combineLatest,
  distinctUntilChanged,
  startWith,
  Subscription,
} from 'rxjs';
import { FlagComponent } from '../flag/flag.component';
import { trackById } from '../helpers/helpers';
import { IconSize } from '../icon';
import { InputEditInlineType, InputSize, InputType } from '../input';
import { SelectItem } from '../select';
import type {
  BackgroundColor,
  BudgetOption,
  ControlType,
  CurrencyInputType,
} from './currency-input.types';

@Component({
  standalone: true,
  selector: 'fl-currency-input',
  imports: [
    FlagComponent,
    IconModule,
    InputComponent,
    ValidationErrorComponent,
  ],
  template: `
    <div
      class="Container"
      [attr.data-background-color]="backgroundColor"
    >
      <div
        class="BudgetContainer"
        [class.IsFocused]="showingDropdown !== 'none'"
        [class.IsInvalid]="hasError"
        [class.IsValid]="isValid"
        [@shakeAnimation]="{
          value: shakeState,
          params: {
            duration: 200,
            translateX: '3px',
          },
        }"
        (@shakeAnimation.done)="shakeDone()"
      >
        <!-- Budget Type -->
        @if (budgetTypeControl) {
          <div
            class="BudgetType Select"
            (click)="handleDropdownClick('budgetType')"
          >
            <div class="BudgetTypeContainer">
              @if (budgetTypeControl?.value) {
                <p class="NoHighlight Select-text">
                  {{ currentBudgetTypeOption?.displayText }}
                </p>
              }
            </div>
            <fl-icon
              class="Chevron"
              [fillIconFont]="true"
              [name]="
                showingDropdown === 'budgetType'
                  ? 'ui-chevron-up'
                  : 'ui-chevron-down'
              "
              [size]="IconSize.MID"
              [useIconFont]="true"
            ></fl-icon>
          </div>

          <!-- Budget Type Dropdown -->
          @if (showingDropdown === 'budgetType') {
            <div class="BudgetType Dropdown">
              @if (budgetTypeOptions; as options) {
                @for (option of options; track trackById($index, option)) {
                  <div
                    class="Dropdown-item"
                    (click)="selectBudgetType(option)"
                  >
                    @if (budgetTypeControl?.value) {
                      <p>
                        {{ option.displayText }}
                      </p>
                    }
                  </div>
                }
              }
            </div>
          }
        }

        @if (label) {
          <label
            class="Label"
            [attr.data-background-color]="backgroundColor"
            [attr.data-type]="type"
            [class.IsBudgetEmpty]="
              isStringControl(budgetControl) && isBudgetEmpty(budgetControl)
            "
            [class.IsInvalid]="hasError"
            [class.IsValid]="isValid"
          >
            {{ label }}
          </label>
        }
        <!-- Budget Input -->
        @if (
          type === 'input' && budgetControl && isStringControl(budgetControl)
        ) {
          <div class="Budget InputContainer">
            <fl-input
              class="Budget Input"
              [beforeLabel]="currencyControl?.value.sign"
              [control]="budgetControl"
              [displayValidation]="false"
              [editInline]="InputEditInlineType.NON_BORDERED"
              [size]="InputSize.LARGE"
              [type]="InputType.CURRENCY"
              (onBlur)="handleBlur()"
            />
          </div>
        }

        <!-- Budget Selector -->
        @if (
          type === 'dropdown' &&
          budgetControl &&
          isBudgetOptionControl(budgetControl)
        ) {
          <div class="BudgetSelectorContainer">
            @if (budgetOptions) {
              <div
                class="Budget Select"
                (click)="handleDropdownClick('budget')"
              >
                @if (currencyControl?.value && budgetControl?.value) {
                  <p class="NoHighlight Select-text">
                    {{ budgetControl.value.displayText }}
                  </p>
                }
                <fl-icon
                  class="Chevron"
                  [fillIconFont]="true"
                  [name]="
                    showingDropdown === 'budget'
                      ? 'ui-chevron-up'
                      : 'ui-chevron-down'
                  "
                  [size]="IconSize.MID"
                  [useIconFont]="true"
                ></fl-icon>
              </div>
            }

            <!-- Budget Dropdown -->
            @if (type === 'dropdown' && showingDropdown === 'budget') {
              <div
                class="Budget Dropdown"
                [class.HasBudgetType]="budgetTypeControl"
              >
                @if (formattedBudgetOptions; as budgets) {
                  @for (budget of budgets; track trackById($index, budget)) {
                    <div
                      class="Dropdown-item"
                      (click)="selectBudget(budget)"
                    >
                      @if (currencyControl?.value && budgetControl?.value) {
                        <p>
                          {{ budget.displayText }}
                        </p>
                      }
                    </div>
                  }
                }
              </div>
            }
          </div>
        }

        <!-- Currency Selector -->
        <div
          class="CurrencySelectContainer"
          [class.Alone]="!budgetControl && !budgetOptions"
        >
          <div
            class="Currency Select"
            [class.Alone]="!budgetControl && !budgetOptions"
            (click)="handleDropdownClick('currency')"
          >
            <div class="CurrencyContainer">
              @if (currencyControl.value?.country && !isMobile) {
                <fl-flag
                  class="mr-xxxsmall"
                  [countryCode]="currencyControl.value?.country"
                ></fl-flag>
              }
              @if (currencyControl.value) {
                <p class="NoHighlight Select-text">
                  {{ currencyControl.value.code }}
                </p>
              }
            </div>
            <fl-icon
              class="Chevron"
              [fillIconFont]="true"
              [name]="
                showingDropdown === 'currency'
                  ? 'ui-chevron-up'
                  : 'ui-chevron-down'
              "
              [size]="IconSize.MID"
              [useIconFont]="true"
            ></fl-icon>
          </div>

          <!-- Currency Dropdown -->
          @if (showingDropdown === 'currency') {
            <div class="Currency Dropdown">
              @if (currencyOptions; as currencies) {
                @for (
                  currency of currencies;
                  track trackById($index, currency)
                ) {
                  <div
                    class="Dropdown-item"
                    (click)="selectCurrency(currency)"
                  >
                    @if (currency.country && !isMobile) {
                      <fl-flag
                        class="mr-xxxsmall"
                        [countryCode]="currency.country"
                      ></fl-flag>
                    }
                    <p>
                      {{ currency.code }}
                    </p>
                  </div>
                }
              }
            </div>
          }
        </div>
      </div>
      @if (isStringControl(budgetControl)) {
        <fl-validation-error [control]="budgetControl"></fl-validation-error>
      }
    </div>
  `,
  styleUrls: ['./currency-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [shakeAnimation],
})
export class CurrencyInputComponent implements OnInit, OnChanges, OnDestroy {
  private cd = inject(ChangeDetectorRef);
  private currencyPipe = inject(FreelancerCurrencyPipe);
  private elementRef = inject(ElementRef);
  private platformId = inject(PLATFORM_ID);
  private screenSizeService = inject(ScreenSizeService);

  IconSize = IconSize;
  InputEditInlineType = InputEditInlineType;
  InputSize = InputSize;
  InputType = InputType;
  ProjectTypeApi = ProjectTypeApi;
  trackById = trackById;

  @Input() backgroundColor: BackgroundColor = 'default';
  @Input() budgetControl?: FormControl<BudgetOption | string>;
  @Input() budgetOptions?: readonly BudgetOption[];
  @Input() budgetTypeControl?: FormControl<ProjectTypeApi>;
  @Input() currencyControl: FormControl<Currency>;
  @Input() currencyFormat: CurrencyFormatOptions = {
    disableCode: true,
  };
  @Input() currencyOptions: readonly Currency[] = [];
  @Input() enableCurrencyConversion = true;
  @Input() label: string;
  @Input() type: CurrencyInputType = 'dropdown';

  formattedBudgetOptions: BudgetOption[] = [];
  isMobile = false;
  isValid = false;
  shakeState: 'void' | 'active' = 'void';
  showingDropdown: 'none' | 'budgetType' | 'budget' | 'currency' = 'none';
  validationStateCheck = false;

  budgetTypeOptions: readonly SelectItem<ProjectTypeApi>[];
  currentBudgetTypeOption: SelectItem<ProjectTypeApi> | undefined;

  private subscriptions = new Subscription();

  @HostListener('document:click', ['$event'])
  handleClickOutside(event: MouseEvent): void {
    // Check if the click was outside the component
    if (
      !this.elementRef.nativeElement.contains(event.target) &&
      this.showingDropdown !== 'none'
    ) {
      // Click was outside, close the dropdown
      this.showingDropdown = 'none';
    }
  }

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.subscriptions.add(
        this.screenSizeService.isMobile().subscribe(value => {
          this.isMobile = value;
          this.formatBudgetOptions();
          this.cd.markForCheck();
        }),
      );
    }

    if (this.isBudgetOptionControl(this.budgetControl) && this.budgetControl) {
      this.formatBudgetOptions();
      this.getBudget();

      this.subscriptions.add(
        combineLatest([
          this.budgetControl.valueChanges.pipe(
            startWith(this.budgetControl.value),
            distinctUntilChanged(),
          ),
          this.currencyControl.valueChanges.pipe(
            startWith(this.currencyControl.value),
            distinctUntilChanged(),
          ),
        ]).subscribe(() => {
          this.formatBudgetOptions();
          // Update view when controls are updated outside the component
          this.cd.markForCheck();
        }),
      );
    }

    if (this.isStringControl(this.budgetControl)) {
      this.subscriptions.add(
        this.budgetControl.statusChanges.subscribe(() => {
          if (
            this.budgetControl?.invalid &&
            (this.budgetControl?.touched || this.budgetControl?.dirty) &&
            this.budgetControl?.value !== '' &&
            isDefined(this.budgetControl?.value)
          ) {
            this.validationStateCheck = true;
          }

          if (this.validationStateCheck && this.budgetControl?.valid) {
            this.isValid = true;
          }

          if (this.validationStateCheck && this.budgetControl?.invalid) {
            this.isValid = false;
          }
        }),
      );
    }

    if (this.budgetTypeControl) {
      this.budgetTypeOptions = [
        {
          value: ProjectTypeApi.FIXED,
          displayText: $localize`:Fixed price option:Pay fixed`,
        },
        {
          value: ProjectTypeApi.HOURLY,
          displayText: $localize`:Hourly price option:Pay hourly`,
        },
      ];

      this.subscriptions.add(
        this.budgetTypeControl.valueChanges
          .pipe(startWith(this.budgetTypeControl.value), distinctUntilChanged())
          .subscribe(budgetType => {
            this.currentBudgetTypeOption = this.budgetTypeOptions.find(
              option => option.value === budgetType,
            );
            this.formatBudgetOptions();
          }),
      );
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.currencyOptions?.currentValue) {
      this.getCurrency();
    }

    if (
      !changes.budgetOptions?.isFirstChange() &&
      changes.budgetOptions?.currentValue
    ) {
      this.formatBudgetOptions();
      this.getBudget();
    }
  }

  getCurrency(): void {
    if (this.currencyControl.value) {
      const matchedCurrency = this.currencyOptions.find(
        currency => currency.code === this.currencyControl.value?.code,
      );
      if (matchedCurrency) {
        this.selectCurrency(matchedCurrency);
      }
    } else if (this.currencyOptions.length > 0) {
      this.selectCurrency(this.currencyOptions[0]);
    }
  }

  getBudget(): void {
    if (this.isBudgetOptionControl(this.budgetControl) && this.budgetOptions) {
      const currentBudgetValue = this.budgetControl.value;

      if (currentBudgetValue) {
        const matchedBudget = this.formattedBudgetOptions.find(
          budget => budget.id === currentBudgetValue.id,
        );
        if (matchedBudget && matchedBudget.id !== currentBudgetValue.id) {
          this.selectBudget(matchedBudget);
        }
      } else if (this.formattedBudgetOptions.length > 0) {
        this.selectBudget(this.formattedBudgetOptions[0]);
      }
    }
  }

  getBudgetTypeText(): string {
    switch (this.budgetTypeControl?.value) {
      case ProjectTypeApi.FIXED:
        return '';
      case ProjectTypeApi.HOURLY:
        return this.isMobile
          ? $localize`:Hourly budget text:/hr`
          : $localize`:Hourly budget text: per hour`;
      default:
        return this.isMobile
          ? $localize`:Hourly budget text:/hr`
          : $localize`:Hourly budget text: per hour`;
    }
  }

  getDisplayedBudgetRange(budget: BudgetOption): string {
    const range = this.enableCurrencyConversion
      ? this.getBudgetRange(budget)
      : budget.range;
    if (!range) {
      return '';
    }

    if (
      budget.id === 'custom' &&
      budget.range.minimum === 0 &&
      budget.range.maximum === 0
    ) {
      return $localize`:Dropdown option to set custom budget:Customize budget`;
    }

    const currencyCode = this.currencyControl.value?.code;

    const formattedRange = this.currencyPipe
      .transform(range, currencyCode, this.currencyFormat)
      ?.trim();

    const budgetTypeText = this.getBudgetTypeText();

    const displayText = this.isMobile
      ? `${formattedRange}${budgetTypeText}`
      : `${budget.name} (${formattedRange}${budgetTypeText})`;

    return displayText;
  }

  selectCurrency(currency: Currency): void {
    this.currencyControl.setValue(currency);
    this.showingDropdown = 'none';
  }

  selectBudget(budget: BudgetOption): void {
    if (this.budgetControl) {
      this.budgetControl.setValue(budget);
    }
    this.showingDropdown = 'none';
  }

  selectBudgetType(option: SelectItem<ProjectTypeApi>): void {
    if (this.budgetTypeControl) {
      this.budgetTypeControl.setValue(option.value);
    }
    this.showingDropdown = 'none';
  }

  getBudgetRange(budget: BudgetOption | undefined): AmountRange | undefined {
    if (!this.currencyControl.value || !budget) {
      return undefined;
    }
    return {
      minimum: this.getConvertedRoundedAmount(
        budget.range.minimum,
        this.currencyControl.value.exchangeRate,
      ),
      maximum: this.getConvertedRoundedAmount(
        budget.range.maximum,
        this.currencyControl.value.exchangeRate,
      ),
    };
  }

  getConvertedRoundedAmount(
    amount?: number,
    targetCurrencyExchangeRate?: number,
  ): number {
    return (amount ?? 1) / (targetCurrencyExchangeRate ?? 1);
  }

  formatBudgetOptions(): void {
    if (
      this.budgetControl &&
      this.isBudgetOptionControl(this.budgetControl) &&
      this.budgetOptions &&
      this.currencyControl.value
    ) {
      this.formattedBudgetOptions = this.budgetOptions.map(budget => ({
        ...budget,
        displayText: this.getDisplayedBudgetRange(budget),
      }));

      const currentBudget = this.budgetControl.value;
      if (currentBudget) {
        this.budgetControl.setValue(
          {
            displayText: this.getDisplayedBudgetRange(currentBudget),
            id: currentBudget.id,
            name: currentBudget.name,
            range: currentBudget.range,
          },
          { emitEvent: false },
        );
      }
    }
  }

  shakeDone(): void {
    this.shakeState = 'void';
  }

  handleBlur(): void {
    if (
      this.isStringControl(this.budgetControl) &&
      this.budgetControl?.invalid &&
      (this.budgetControl?.touched || this.budgetControl?.dirty)
    ) {
      this.shakeState = 'active';
    }
  }

  handleDropdownClick(dropdown: 'budgetType' | 'budget' | 'currency'): void {
    if (this.showingDropdown === dropdown) {
      this.showingDropdown = 'none';
      return;
    }

    this.showingDropdown = dropdown;
  }

  isBudgetEmpty(control: FormControl<string>): boolean {
    return !control.value || control.value.length === 0;
  }

  isBudgetOptionControl(
    control?: FormControl<BudgetOption | string>,
  ): control is FormControl<BudgetOption> {
    return this.controlType === 'budgetOption';
  }

  isStringControl(
    control?: FormControl<BudgetOption | string>,
  ): control is FormControl<string> {
    return this.controlType === 'string';
  }

  get controlType(): ControlType {
    switch (this.type) {
      case 'input':
        return 'string';
      case 'dropdown':
        return 'budgetOption';
      default:
        return 'string';
    }
  }

  get hasError(): boolean {
    if (
      isDefined(this.budgetControl) &&
      this.isStringControl(this.budgetControl)
    ) {
      return (
        this.budgetControl.invalid &&
        (this.budgetControl.touched || this.budgetControl.dirty) &&
        isDefined(this.budgetControl.value)
      );
    }
    return false;
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
